import { Component, DestroyRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BlendersService } from '../../core/services/blenders.service';
import { PackagingMachinesService } from '../../core/services/packaging-machines.service';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ValueStream } from '../../core/enums/general.enum';

@Component({
  selector: 'app-apo-parameters',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule],
  templateUrl: './apo-parameters.component.html',
  styleUrl: './apo-parameters.component.scss'
})
export class ApoParametersComponent implements OnChanges {
  @Input() selectedVS: ValueStream = ValueStream.VS1;
  @Output() formValidityChange = new EventEmitter<boolean>();

  private readonly destroyRef = inject(DestroyRef);

  parametersFormGroup: FormGroup = this.fb.group({
    [ValueStream.VS1]: this.fb.group({
      blendersForm: this.fb.array([]),
      packagingMachinesForm: this.fb.array([]),
      qualityForm: this.fb.array([]),
    }),
    [ValueStream.VS2]: this.fb.group({
      blendersForm: this.fb.array([]),
      packagingMachinesForm: this.fb.array([]),
      qualityForm: this.fb.array([]),
    }),
  });

  blenders: any[] = [];
  packagingMachines: any[] = [];

  blendersBulk: { delay: number | null; oee: number | null } = { delay: null, oee: null };
  packagingBulk: { delay: number | null; oee: number | null } = { delay: null, oee: null };

  get currentVSGroup(): FormGroup {
    return this.parametersFormGroup.get(this.selectedVS) as FormGroup;
  }

  get blendersForm(): FormArray {
    return this.currentVSGroup.get('blendersForm') as FormArray;
  }

  get packagingMachinesForm(): FormArray {
    return this.currentVSGroup.get('packagingMachinesForm') as FormArray;
  }

  get qualityForm(): FormArray {
    return this.currentVSGroup.get('qualityForm') as FormArray;
  }

  constructor(
    private blendersService: BlendersService,
    private packagingMachinesService: PackagingMachinesService,
    private fb: FormBuilder
  ) {
    this.parametersFormGroup.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitValidityAsync());
  }

  ngOnInit() {
    this.emitValidityAsync();
    this.getBlenders();
    this.getPackagingMachines();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedVS'] && !changes['selectedVS'].firstChange) {
      this.blendersBulk = { delay: null, oee: null };
      this.packagingBulk = { delay: null, oee: null };
    }
  }

  getBlenders() {
    this.blendersService.getBlenders().subscribe((res: any) => {
      this.blenders = res.sort((a: any, b: any) => a.id - b.id);
      this.buildAllForms();
    });
  }

  getPackagingMachines() {
    this.packagingMachinesService.getPackagingMachines().subscribe((res: any) => {
      this.packagingMachines = res.sort((a: any, b: any) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }));
      this.buildAllForms();
    });
  }

  buildAllForms() {
    if (!this.blenders.length || !this.packagingMachines.length) return;

    for (const vs of Object.values(ValueStream)) {
      const vsGroup = this.parametersFormGroup.get(vs) as FormGroup;
      const blendersArray = vsGroup.get('blendersForm') as FormArray;
      const packagingArray = vsGroup.get('packagingMachinesForm') as FormArray;
      const qualityArray = vsGroup.get('qualityForm') as FormArray;

      blendersArray.clear();
      packagingArray.clear();
      qualityArray.clear();

      for (const blender of this.blenders.filter(b => b.value_stream === vs)) {
        blendersArray.push(this.fb.group({
          [blender.id]: this.fb.group({ delay: [0, Validators.required], oee: [1, Validators.required] })
        }));
      }

      for (const machine of this.packagingMachines.filter(p => p.value_stream === vs)) {
        packagingArray.push(this.fb.group({
          [machine.id]: this.fb.group({ delay: [0, Validators.required], oee: [1, Validators.required] })
        }));
      }

      qualityArray.push(this.fb.group({
        ['Calidad']: this.fb.group({ delay: [0, Validators.required], oee: [1, Validators.required] })
      }));
    }
    this.emitValidityAsync();
  }

  /** Deferred so parent bindings do not update mid–change detection (NG0100). */
  private emitValidityAsync(): void {
    queueMicrotask(() => {
      this.formValidityChange.emit(this.parametersFormGroup.valid);
    });
  }

  getFormGroupKeys(group: any): string[] {
    return Object.keys(group.controls);
  }

  onBlendersBulkInput(field: 'delay' | 'oee', rawValue: string): void {
    const value = parseFloat(rawValue);
    if (rawValue === '' || isNaN(value)) return;
    this.blendersBulk[field] = value;
    this.applySectionValues(this.blendersForm, field, value);
  }

  onPackagingBulkInput(field: 'delay' | 'oee', rawValue: string): void {
    const value = parseFloat(rawValue);
    if (rawValue === '' || isNaN(value)) return;
    this.packagingBulk[field] = value;
    this.applySectionValues(this.packagingMachinesForm, field, value);
  }

  private applySectionValues(formArray: FormArray, field: 'delay' | 'oee', value: number): void {
    for (const control of formArray.controls) {
      const group = control as FormGroup;
      for (const key of this.getFormGroupKeys(group)) {
        (group.get(key) as FormGroup).patchValue({ [field]: value });
      }
    }
  }
}
